<template>
  <div class="mc-control-tower__detail-notification">
    <div class="mc-control-tower__detail-notification-header">
      <h5>{{ $t('global.object.notification') }}</h5>
      <div class="mb-2">
        <div class="d-flex justify-content-end mb-1">
          <a class="text-primary font-size-12" @click="redirectNotificationLog">
            {{ $t('dashboard.action.viewAll') }}
          </a>
        </div>
        <div class="d-flex gap-2">
          <span>
            {{ `${$t('controlTower.detail.notChecked')} (${ctData.totalUnresolvedAlerts ?? 0})` }}
          </span>
          <div class="form-check form-switch form-switch-sm">
            <input v-model="isUnresolved" class="form-check-input" type="checkbox" />
          </div>
        </div>
      </div>
    </div>
    <template v-if="!isUnresolved">
      <div
        v-if="hasAccess(['e4e-alert-center-urgent:read'])"
        class="mc-control-tower__detail-notification-item"
      >
        <div class="mc-control-tower__detail-notification-title">
          <span>{{
            `${$t('setting.list.notification.form.urgent')} (${urgentAlerts.length})`
          }}</span>
          <i
            :class="`mc-fa icon-outline_${alertsExpand.urgent ? 'chevron-up' : 'chevron-down'}`"
            @click="handleCollapseExpand('urgent')"
          ></i>
        </div>
        <template v-if="alertsExpand.urgent">
          <ul v-if="urgentAlerts.length > 0" class="mc-control-tower__detail-notification-list">
            <li
              v-for="(alert, index) in urgentAlerts"
              v-show="index < 5"
              :key="alert.rawAlertId"
              :class="notificationItemClass(alert, 'urgent')"
              @click="handleClick(alert)"
            >
              <b-row :key="alert.rawAlertId" class="align-items-center">
                <b-col md="4">
                  <span>
                    {{
                      isToday(alert.createdOn)
                        ? `${momentTime(alert.createdOn)} WIB`
                        : momentDateShortMonth(alert.createdOn)
                    }}
                  </span>
                </b-col>
                <b-col md="8">
                  <div class="mc-control-tower__detail-notification-list-name">
                    <span class="icon_urgent">
                      <img :src="alert.alert.iconUrl" :alt="alert.alert.descriptionIdn" />
                    </span>
                    <strong>
                      {{ displayAlertTitle(alert) }}
                    </strong>
                    <i
                      v-if="alert.isResolved"
                      :title="$t('controlTower.detail.checked')"
                      class="mc-fa icon-fill_checkmark-circle"
                    ></i>
                  </div>
                </b-col>
              </b-row>
            </li>
          </ul>
          <div v-else class="mc-control-tower__detail-notification-list--empty">
            <figure>
              <img
                :src="urgentAlertRules.length > 0 ? resolvedPath : noNotificationPath"
                :title="$t('controlTower.detail.emptyNotification')"
                alt="Empty Notification"
              />
            </figure>
            <div class="ms-2">
              <template v-if="urgentAlertRules.length > 0">
                <p>{{ $t('notification.text.noNotificationUrgentToResolved') }}</p>
              </template>
              <template v-else>
                <h6>{{ $t('controlTower.detail.notificationNotAdded') }}</h6>
                <span class="text-muted">
                  <p v-if="getAccessRight()">
                    {{ $t('controlTower.detail.urgentNotAdded') }}
                  </p>
                  <p v-else class="mt-2">
                    {{ $t('notification.text.contactAdmin') }}
                  </p>
                </span>
              </template>
            </div>
          </div>
        </template>
      </div>
      <div v-else class="mc-control-tower__detail-notification-list--coming-soon">
        <figure>
          <img
            :src="comingSoonPath"
            :title="$t('controlTower.detail.emptyNotification')"
            alt="Empty Notification"
          />
        </figure>
        <div class="ms-2">
          <h6>{{ $t('controlTower.detail.comingSoon') }}</h6>
          <p class="text-muted">
            {{ $t('controlTower.detail.comingSoonDesc') }}
          </p>
        </div>
      </div>
      <div class="mc-control-tower__detail-notification-item">
        <div class="mc-control-tower__detail-notification-title">
          <span>{{
            `${$t('setting.list.notification.form.important')} (${importantAlerts.length})`
          }}</span>
          <i
            v-if="hasAccess(['e4e-alert-center-urgent:read'])"
            :class="`mc-fa icon-outline_${alertsExpand.important ? 'chevron-up' : 'chevron-down'}`"
            @click="handleCollapseExpand('important')"
          ></i>
        </div>
        <template v-if="alertsExpand.important">
          <ul v-if="importantAlerts.length > 0" class="mc-control-tower__detail-notification-list">
            <li
              v-for="(alert, index) in importantAlerts"
              v-show="index < 5"
              :key="alert.rawAlertId"
              :class="notificationItemClass(alert, 'important')"
              @click="handleClick(alert)"
            >
              <b-row :key="alert.rawAlertId" class="align-items-center">
                <b-col md="4">
                  <span>
                    {{
                      isToday(alert.createdOn)
                        ? `${momentTime(alert.createdOn)} WIB`
                        : momentDateShortMonth(alert.createdOn)
                    }}
                  </span>
                </b-col>
                <b-col md="8">
                  <div class="mc-control-tower__detail-notification-list-name">
                    <span
                      :class="
                        hasAccess(['e4e-alert-center-urgent:read'])
                          ? 'icon_important'
                          : 'icon_urgent'
                      "
                    >
                      <img :src="alert.alert.iconUrl" :alt="alert.alert.descriptionIdn" />
                    </span>
                    <strong>
                      {{ displayAlertTitle(alert) }}
                    </strong>
                    <i
                      v-if="alert.isResolved"
                      :title="$t('controlTower.detail.checked')"
                      class="mc-fa icon-fill_checkmark-circle"
                    ></i>
                  </div>
                </b-col>
              </b-row>
            </li>
          </ul>
          <div v-else class="mc-control-tower__detail-notification-list--empty">
            <figure>
              <img
                :src="importantAlertRules.length > 0 ? resolvedPath : noNotificationPath"
                :title="$t('controlTower.detail.emptyNotification')"
                alt="Empty Notification"
              />
            </figure>
            <div class="ms-2">
              <template v-if="importantAlertRules.length > 0">
                <p>{{ $t('notification.text.noNotificationImportantToResolve') }}</p>
              </template>
              <template v-else>
                <h6>{{ $t('controlTower.detail.notificationNotAdded') }}</h6>
                <span class="text-muted">
                  <p v-if="getAccessRight()">
                    {{ $t('controlTower.detail.importantNotAdded') }}
                  </p>
                  <p v-else class="mt-2">
                    {{ $t('notification.text.contactAdmin') }}
                  </p>
                </span>
              </template>
            </div>
          </div>
        </template>
      </div>
    </template>
    <template v-else>
      <div
        v-if="hasAccess(['e4e-alert-center-urgent:read'])"
        class="mc-control-tower__detail-notification-item"
      >
        <div class="mc-control-tower__detail-notification-title">
          <span>{{
            `${$t('setting.list.notification.form.urgent')} (${urgentUnresolvedAlerts.length})`
          }}</span>
          <i
            :class="`mc-fa icon-outline_${
              unresolvedAlertsExpand.urgent ? 'chevron-up' : 'chevron-down'
            }`"
            @click="handleCollapseExpand('urgent')"
          ></i>
        </div>
        <template v-if="unresolvedAlertsExpand.urgent">
          <ul
            v-if="urgentUnresolvedAlerts.length > 0"
            class="mc-control-tower__detail-notification-list"
          >
            <li
              v-for="(alert, index) in urgentUnresolvedAlerts"
              v-show="index < 5"
              :key="alert.rawAlertId"
              :class="notificationItemClass(alert, 'urgent')"
              @click="handleClick(alert)"
            >
              <b-row :key="alert.rawAlertId" class="align-items-center">
                <b-col md="4">
                  <span>
                    {{
                      isToday(alert.createdOn)
                        ? `${momentTime(alert.createdOn)} WIB`
                        : momentDateShortMonth(alert.createdOn)
                    }}
                  </span>
                </b-col>
                <b-col md="8">
                  <div class="mc-control-tower__detail-notification-list-name">
                    <span class="icon_urgent">
                      <img :src="alert.alert.iconUrl" :alt="alert.alert.descriptionIdn" />
                    </span>
                    <strong>
                      {{ displayAlertTitle(alert) }}
                    </strong>
                    <i
                      v-if="alert.isResolved"
                      :title="$t('controlTower.detail.checked')"
                      class="mc-fa icon-fill_checkmark-circle"
                    ></i>
                  </div>
                </b-col>
              </b-row>
            </li>
          </ul>
          <div v-else class="mc-control-tower__detail-notification-list--empty">
            <figure>
              <img
                :src="urgentAlertRules.length > 0 ? resolvedPath : noNotificationPath"
                :title="$t('controlTower.detail.emptyNotification')"
                alt="Empty Notification"
              />
            </figure>
            <div class="ms-2">
              <template v-if="urgentAlertRules.length > 0">
                <p>{{ $t('notification.text.nothingNotificationUrgentResolved') }}</p>
              </template>
              <template v-else>
                <div>
                  <strong>{{ $t('setting.list.notification.notificationNotAdded') }}</strong>
                  <span class="text-muted">
                    <p v-if="getAccessRight()">
                      {{ $t('controlTower.detail.urgentNotAdded') }}
                    </p>
                    <p v-else class="mt-2">
                      {{ $t('notification.text.contactAdmin') }}
                    </p>
                  </span>
                </div>
              </template>
            </div>
          </div>
        </template>
      </div>
      <div v-else class="mc-control-tower__detail-notification-list--coming-soon">
        <figure>
          <img
            :src="comingSoonPath"
            :title="$t('controlTower.detail.emptyNotification')"
            alt="Empty Notification"
          />
        </figure>
        <div class="ms-2">
          <h6>{{ $t('controlTower.detail.comingSoon') }}</h6>
          <p class="text-muted">
            {{ $t('controlTower.detail.comingSoonDesc') }}
          </p>
        </div>
      </div>
      <div class="mc-control-tower__detail-notification-item">
        <div class="mc-control-tower__detail-notification-title">
          <span>{{
            `${$t('setting.list.notification.form.important')} (${
              importantUnresolvedAlerts.length
            })`
          }}</span>
          <i
            v-if="hasAccess(['e4e-alert-center-urgent:read'])"
            :class="`mc-fa icon-outline_${
              unresolvedAlertsExpand.important ? 'chevron-up' : 'chevron-down'
            }`"
            @click="handleCollapseExpand('important')"
          ></i>
        </div>
        <template v-if="unresolvedAlertsExpand.important">
          <ul
            v-if="importantUnresolvedAlerts.length > 0"
            class="mc-control-tower__detail-notification-list"
          >
            <li
              v-for="(alert, index) in importantUnresolvedAlerts"
              v-show="index < 5"
              :key="alert.rawAlertId"
              :class="notificationItemClass(alert, 'important')"
              @click="handleClick(alert)"
            >
              <b-row :key="alert.rawAlertId" class="align-items-center">
                <b-col md="4">
                  <span>
                    {{
                      isToday(alert.createdOn)
                        ? `${momentTime(alert.createdOn)} WIB`
                        : momentDateShortMonth(alert.createdOn)
                    }}
                  </span>
                </b-col>
                <b-col md="8">
                  <div class="mc-control-tower__detail-notification-list-name">
                    <span
                      :class="
                        hasAccess(['e4e-alert-center-urgent:read'])
                          ? 'icon_important'
                          : 'icon_urgent'
                      "
                    >
                      <img :src="alert.alert.iconUrl" :alt="alert.alert.descriptionIdn" />
                    </span>
                    <strong>
                      {{ displayAlertTitle(alert) }}
                    </strong>
                    <i
                      v-if="alert.isResolved"
                      :title="$t('controlTower.detail.checked')"
                      class="mc-fa icon-fill_checkmark-circle"
                    ></i>
                  </div>
                </b-col>
              </b-row>
            </li>
          </ul>
          <div v-else class="mc-control-tower__detail-notification-list--empty">
            <figure>
              <img
                :src="importantAlertRules.length > 0 ? resolvedPath : noNotificationPath"
                :title="$t('controlTower.detail.emptyNotification')"
                alt="Empty Notification"
              />
            </figure>
            <div class="ms-2">
              <template v-if="importantAlertRules.length > 0">
                <p>{{ $t('notification.text.nothingNotificationImportantResolved') }}</p>
              </template>
              <template v-else>
                <div>
                  <strong>{{ $t('setting.list.notification.notificationNotAdded') }}</strong>
                  <span class="text-muted">
                    <p v-if="getAccessRight()">
                      {{ $t('controlTower.detail.importantNotAdded') }}
                    </p>
                    <p v-else class="mt-2">
                      {{ $t('notification.text.contactAdmin') }}
                    </p>
                  </span>
                </div>
              </template>
            </div>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>
<script>
import { Helper } from '@/mixin/helper'
import { MixPanel } from '@/mixin/mixpanel'
import { EventBus } from '@/router/views/fleet-task/lib/event-bus'
import moment from 'moment'
import { mapGetters } from 'vuex'

export default {
  mixins: [Helper, MixPanel],
  props: {
    ctData: {
      type: Object,
      required: true,
    },
    licensePlate: {
      type: String,
      required: true,
    },
    vehicleId: {
      type: Number,
      required: true,
    },
    urgentAlertRules: {
      type: Array,
      default: () => [],
    },
    importantAlertRules: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isUnresolved: false,
      alertsExpand: {
        urgent: true,
        important: false,
      },
      unresolvedAlertsExpand: {
        urgent: true,
        important: false,
      },
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    comingSoonPath() {
      return require('@/assets/images/bell-icon.svg')
    },
    noNotificationPath() {
      return require('@/assets/images/illustration/whitelabel/no-notification.png')
    },
    resolvedPath() {
      return require('@/assets/images/success-icon.png')
    },
    urgentAlerts() {
      return this.ctData.urgent?.alerts || []
    },
    importantAlerts() {
      return this.ctData.important?.alerts || []
    },
    urgentUnresolvedAlerts() {
      return this.ctData.urgent?.unresolvedAlerts || []
    },
    importantUnresolvedAlerts() {
      return this.ctData.important?.unresolvedAlerts || []
    },
    hasRuleNameScope() {
      return this.hasAccess('e4J-alert-rule-name-as-title:read')
    },
  },
  mounted() {
    if (!this.hasAccess(['e4e-alert-center-urgent:read'])) {
      this.alertsExpand = {
        urgent: false,
        important: true,
      }
      this.unresolvedAlertsExpand = {
        urgent: false,
        important: true,
      }
    }
  },
  methods: {
    isToday(date) {
      return moment(date).isSame(moment(), 'day')
    },
    handleClick(data) {
      const isOpened = this.isAlertActive(data)
      if (isOpened) {
        EventBus.$emit('handle-close-popup', data.rawAlertId ?? data.id)
        return
      }
      EventBus.$emit('handle-open-popup', data.rawAlertId ?? data.id)
      this.track('Click Notificion on Control Tower V2')
    },
    isAlertActive(alert) {
      const alertId = String(alert.rawAlertId ?? alert.id)
      return this.$store.state.notification.activePopups.some((popup) => {
        return (
          String(popup.id) === alertId ||
          String(popup.alert?.rawAlertId ?? popup.alert?.id) === alertId
        )
      })
    },
    notificationItemClass(alert, type) {
      const isActive = this.isAlertActive(alert)
      return {
        unresolved: !alert.isResolved,
        active: isActive,
        'active--urgent': isActive && type === 'urgent',
        'active--important': isActive && type === 'important',
      }
    },
    redirectNotificationLog() {
      window.open(
        this.$router.resolve({
          name: 'Notification Log',
          query: {
            id: this.vehicleId,
            licensePlate: this.licensePlate,
            isImportant: true,
            isUnresolved: this.isUnresolved ? true : undefined,
          },
        }).href,
        '_blank'
      )
    },
    handleCollapseExpand(handler) {
      if (this.isUnresolved) {
        this.$set(this.unresolvedAlertsExpand, handler, !this.unresolvedAlertsExpand[handler])
      } else {
        this.$set(this.alertsExpand, handler, !this.alertsExpand[handler])
      }
    },
    displayAlertTitle(alert) {
      if (this.hasRuleNameScope) {
        return alert.alertRule
          ? alert.alertRule.label
          : this.$i18n.locale === 'id'
          ? alert.alert.descriptionIdn
          : alert.alert.descriptionEng
      } else {
        return this.$i18n.locale === 'id' ? alert.alert.descriptionIdn : alert.alert.descriptionEng
      }
    },
  },
}
</script>
